import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { StressAnalytics } from '../types';
import { endpointLabel, formatClock, formatSeconds } from './format';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_ERROR,
  CHART_GRID_PROPS,
  CHART_LATENCY_COLORS,
  CHART_LEGEND_STYLE,
  CHART_NEUTRAL,
  CHART_SUCCESS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '../../components/stats/chartTheme';
import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
  ChartSliceGradient,
} from '../../components/stats/ChartPrimitives';
import { chartGradientFill, useChartGradientId } from '../../components/stats/chartGradient';

const CHART_HEIGHT = 240;
const PANEL_ICON = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 18V9m5 9V5m5 13v-7m5 7V3" />
  </svg>
);

function Panel({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <ChartPanel
      title={title}
      subtitle={hint}
      icon={PANEL_ICON}
      iconClassName="bg-indigo-50 text-indigo-700"
    >
      {children}
    </ChartPanel>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex" style={{ height: CHART_HEIGHT }}>
      <ChartEmptyState>{message}</ChartEmptyState>
    </div>
  );
}

/**
 * Calls submitted per bucket, split by how they ended. Under a healthy system
 * this tracks the burst schedule; a plateau below the configured rate means
 * something upstream is throttling.
 */
export function ThroughputChart({ analytics }: { analytics: StressAnalytics }) {
  const successGradientId = useChartGradientId('stress-throughput-success');
  const errorGradientId = useChartGradientId('stress-throughput-error');
  const inFlightGradientId = useChartGradientId('stress-throughput-inflight');
  const data = analytics.series.map((point) => ({
    ts: formatClock(point.ts),
    success: point.success,
    error: point.error,
    inFlight: Math.max(0, point.submitted - point.success - point.error),
  }));

  return (
    <Panel
      title="Throughput"
      hint={`Calls per ${analytics.bucket_seconds}s bucket, by outcome.`}
    >
      {data.length === 0 ? (
        <EmptyChart message="No calls recorded yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart
            data={data}
            margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
            barCategoryGap="18%"
          >
            <defs>
              <ChartBarGradient id={successGradientId} color={CHART_SUCCESS} />
              <ChartBarGradient id={errorGradientId} color={CHART_ERROR} />
              <ChartBarGradient id={inFlightGradientId} color={CHART_NEUTRAL} />
            </defs>
            <CartesianGrid {...CHART_GRID_PROPS} />
            <XAxis {...CHART_AXIS_PROPS} dataKey="ts" tick={CHART_AXIS_TICK} />
            <YAxis {...CHART_AXIS_PROPS} tick={CHART_AXIS_TICK} allowDecimals={false} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL_STYLE} />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Bar
              {...CHART_BAR_PROPS}
              dataKey="success"
              name="Success"
              stackId="a"
              fill={chartGradientFill(successGradientId)}
              isAnimationActive={false}
            />
            <Bar
              {...CHART_BAR_PROPS}
              dataKey="error"
              name="Error"
              stackId="a"
              fill={chartGradientFill(errorGradientId)}
              isAnimationActive={false}
            />
            <Bar
              {...CHART_BAR_PROPS}
              dataKey="inFlight"
              name="In flight"
              stackId="a"
              fill={chartGradientFill(inFlightGradientId)}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

/**
 * The chart that actually answers "did load hurt?". Execution time is roughly
 * fixed in dummy mode, so a queue line climbing away from it is the service
 * running out of concurrency rather than the provider getting slower.
 */
export function LatencyOverTimeChart({ analytics }: { analytics: StressAnalytics }) {
  const data = analytics.series.map((point) => ({
    ts: formatClock(point.ts),
    execP50: point.exec_p50,
    execP90: point.exec_p90,
    queueP50: point.queue_p50,
    queueP90: point.queue_p90,
  }));
  const hasAny = data.some((d) => d.execP50 != null || d.queueP50 != null);

  return (
    <Panel
      title="Latency over time"
      hint="Execution vs queue wait, in seconds. Queue climbing while execution stays flat means the service is saturated."
    >
      {!hasAny ? (
        <EmptyChart message="No finished calls yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid {...CHART_GRID_PROPS} />
            <XAxis {...CHART_AXIS_PROPS} dataKey="ts" tick={CHART_AXIS_TICK} />
            <YAxis
              {...CHART_AXIS_PROPS}
              tick={CHART_AXIS_TICK}
              tickFormatter={(v: number) => `${v.toFixed(1)}s`}
            />
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL_STYLE}
              formatter={(v) => formatSeconds(typeof v === 'number' ? v : null)}
            />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Line
              type="monotone"
              dataKey="execP50"
              name="Exec p50"
              stroke={CHART_LATENCY_COLORS.execution}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="execP90"
              name="Exec p90"
              stroke={CHART_LATENCY_COLORS.execution}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="queueP50"
              name="Queue p50"
              stroke={CHART_LATENCY_COLORS.queue}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="queueP90"
              name="Queue p90"
              stroke={CHART_LATENCY_COLORS.queue}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

/** Percentile bars for the three timings the backend reports separately. */
export function PercentileChart({ analytics }: { analytics: StressAnalytics }) {
  const queueGradientId = useChartGradientId('stress-percentile-queue');
  const executionGradientId = useChartGradientId('stress-percentile-execution');
  const totalGradientId = useChartGradientId('stress-percentile-total');
  const t = analytics.totals;
  const data = [
    { label: 'p50', exec: t.exec_p50, queue: t.queue_p50, total: t.total_p50 },
    { label: 'p90', exec: t.exec_p90, queue: t.queue_p90, total: t.total_p90 },
    { label: 'p95', exec: t.exec_p95, queue: t.queue_p95, total: t.total_p95 },
    { label: 'p99', exec: t.exec_p99, queue: t.queue_p99, total: t.total_p99 },
  ];
  const hasAny = data.some((d) => d.exec != null || d.queue != null || d.total != null);

  return (
    <Panel
      title="Latency percentiles"
      hint="End-to-end is queue wait plus execution, so the tail shows where time actually went."
    >
      {!hasAny ? (
        <EmptyChart message="No finished calls yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart
            data={data}
            margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
            barCategoryGap="18%"
          >
            <defs>
              <ChartBarGradient id={queueGradientId} color={CHART_LATENCY_COLORS.queue} />
              <ChartBarGradient id={executionGradientId} color={CHART_LATENCY_COLORS.execution} />
              <ChartBarGradient id={totalGradientId} color={CHART_LATENCY_COLORS.total} />
            </defs>
            <CartesianGrid {...CHART_GRID_PROPS} />
            <XAxis {...CHART_AXIS_PROPS} dataKey="label" tick={CHART_AXIS_TICK} />
            <YAxis
              {...CHART_AXIS_PROPS}
              tick={CHART_AXIS_TICK}
              tickFormatter={(v: number) => `${v.toFixed(1)}s`}
            />
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL_STYLE}
              formatter={(v) => formatSeconds(typeof v === 'number' ? v : null)}
            />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Bar
              {...CHART_BAR_PROPS}
              dataKey="queue"
              name="Queue wait"
              fill={chartGradientFill(queueGradientId)}
              isAnimationActive={false}
            />
            <Bar
              {...CHART_BAR_PROPS}
              dataKey="exec"
              name="Execution"
              fill={chartGradientFill(executionGradientId)}
              isAnimationActive={false}
            />
            <Bar
              {...CHART_BAR_PROPS}
              dataKey="total"
              name="End to end"
              fill={chartGradientFill(totalGradientId)}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

export function OutcomeChart({ analytics }: { analytics: StressAnalytics }) {
  const gradientBaseId = useChartGradientId('stress-outcome');
  const t = analytics.totals;
  const data = [
    { name: 'Success', value: t.success, fill: CHART_SUCCESS },
    { name: 'Error', value: t.errors, fill: CHART_ERROR },
    { name: 'In flight', value: t.in_flight, fill: CHART_NEUTRAL },
  ].filter((slice) => slice.value > 0);

  return (
    <Panel title="Outcomes" hint={`${t.total} recorded call${t.total === 1 ? '' : 's'}.`}>
      {data.length === 0 ? (
        <EmptyChart message="No calls recorded yet." />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <PieChart>
            <defs>
              {data.map((slice) => (
                <ChartSliceGradient
                  key={slice.name}
                  id={`${gradientBaseId}-${slice.name.toLowerCase().replace(/\s+/g, '-')}`}
                  color={slice.fill}
                />
              ))}
            </defs>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={55}
              outerRadius={85}
              isAnimationActive={false}
            >
              {data.map((slice) => (
                <Cell
                  key={slice.name}
                  fill={chartGradientFill(
                    `${gradientBaseId}-${slice.name.toLowerCase().replace(/\s+/g, '-')}`,
                  )}
                />
              ))}
            </Pie>
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL_STYLE} />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

/**
 * How the run was distributed across endpoints and how each one held up. Only
 * worth showing once more than one endpoint was hit — i.e. for randomized runs.
 */
export function ModelBreakdownTable({ analytics }: { analytics: StressAnalytics }) {
  const groups = analytics.by_model;
  if (groups.length < 2) return null;

  return (
    <Panel
      title="By endpoint"
      hint="Which endpoints the randomized run hit, and how each one performed."
    >
      <table className="data-table w-full text-sm">
        <thead className="text-xs text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left font-medium pb-2">Endpoint</th>
            <th className="text-right font-medium pb-2">Calls</th>
            <th className="text-right font-medium pb-2">Errors</th>
            <th className="text-right font-medium pb-2">Exec p50</th>
            <th className="text-right font-medium pb-2">Exec p90</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {groups.map((group) => (
            <tr key={group.model_type}>
              <td data-label="Endpoint" className="py-2 pr-4 text-slate-700">
                {endpointLabel(group.model_type)}
              </td>
              <td data-label="Calls" className="py-2 text-right text-slate-600 tabular-nums">
                {group.total}
              </td>
              <td
                data-label="Errors"
                className={`py-2 text-right tabular-nums ${
                  group.errors > 0 ? 'text-danger' : 'text-slate-400'
                }`}
              >
                {group.errors}
              </td>
              <td data-label="Exec p50" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(group.exec_p50)}
              </td>
              <td data-label="Exec p90" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(group.exec_p90)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

export function ErrorGroupsTable({ analytics }: { analytics: StressAnalytics }) {
  if (analytics.error_groups.length === 0) return null;
  return (
    <Panel
      title="Failures"
      hint="Grouped by message, so one repeated fault doesn't look like many."
    >
      <table className="data-table w-full text-sm">
        <thead className="text-xs text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left font-medium pb-2">Error</th>
            <th className="text-right font-medium pb-2 w-20">Count</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {analytics.error_groups.map((group) => (
            <tr key={group.error}>
              <td data-label="Error" className="py-2 pr-4 text-slate-700 font-mono text-xs break-all">
                {group.error}
              </td>
              <td data-label="Count" className="py-2 text-right text-slate-600 tabular-nums">
                {group.count}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

export function SlowestTable({ analytics }: { analytics: StressAnalytics }) {
  if (analytics.slowest.length === 0) return null;
  return (
    <Panel title="Slowest calls" hint="Execution time only, excluding the queue wait.">
      <table className="data-table w-full text-sm">
        <thead className="text-xs text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left font-medium pb-2">Call</th>
            <th className="text-left font-medium pb-2">Status</th>
            <th className="text-right font-medium pb-2">Execution</th>
            <th className="text-right font-medium pb-2">End to end</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {analytics.slowest.map((call) => (
            <tr key={call.id}>
              <td data-label="Call" className="py-2 pr-4 font-mono text-xs text-slate-500">
                {call.id.slice(0, 8)}
              </td>
              <td data-label="Status" className="py-2 pr-4 text-slate-600">
                {call.status}
              </td>
              <td data-label="Execution" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(call.duration_seconds)}
              </td>
              <td data-label="End to end" className="py-2 text-right text-slate-600 tabular-nums">
                {formatSeconds(call.total_seconds)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}
